import { StyleSheet } from 'aphrodite';
import { THEME } from '../../components-v2/Theme/theme';

export const styleSheet = StyleSheet.create({
	// --- Page + toolbar -----------------------------------------------------
	// Composes with components-v2 Page (which already supplies flex:1, column,
	// and horizontal padding) — we only add vertical rhythm + a scroll boundary.
	page: {
		gap: THEME.spacing.md,
		paddingTop: THEME.spacing.lg,
		paddingBottom: THEME.spacing.lg,
		minHeight: 0,
		// Let the page shrink within its grid track so the app-drawer column can claim its
		// width; without this the wide toolbar forces the 1fr content track to stay wide and
		// the page overflows horizontally underneath the drawer.
		minWidth: 0,
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		flexWrap: 'wrap',
	},
	toolbarGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap',
	},
	userSelect: {
		position: 'relative',
		width: 200,
		// Pin the combobox trigger to 32px (border-box) so it matches the toolbar's sm buttons
		// and the view toggle. Aphrodite only treats keys starting with ':'/'@' as nested
		// selectors, so target the descendant via ':has(...)' (the pattern the Combobox styles use).
		':has([data-slot="input-group"]) [data-slot="input-group"]': {
			boxSizing: 'border-box',
			height: 32,
		},
	},
	// Widen the dropdown past the narrow 200px trigger so user names/emails aren't cramped
	// (the combobox popup otherwise sizes to the anchor width).
	userSelectDropdown: {
		minWidth: 280,
	},
	// Shrink the shared TabToggle (its default ~40px medium size towers over the 32px controls)
	// to match the rest of the toolbar. Root border adds 2px, so 30 + 2 = 32.
	viewToggle: {
		':has([data-slot="tab-toggle-item"]) [data-slot="tab-toggle-item"]': {
			paddingTop: 0,
			paddingBottom: 0,
			height: 30,
		},
	},
	// In-field clear (✕), overlaid just left of the combobox's chevron. Rendered as a
	// size='icon' <Button>, so we pin the icon back to 12px (icon size forces 16) and the
	// box to 18px, and drop the button chrome.
	userSelectClear: {
		position: 'absolute',
		top: '50%',
		right: 30,
		transform: 'translateY(-50%)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 18,
		height: 18,
		padding: 0,
		border: 'none',
		background: 'none',
		borderRadius: '50%',
		cursor: 'pointer',
		color: THEME.colors.icon.gray,
		zIndex: 2,
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
		':has(*) > svg': {
			width: 12,
			height: 12,
		},
	},
	legend: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.xs,
	},
	legendChipOff: {
		opacity: 0.45,
	},
	legendDot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
	},
	title: {
		margin: 0,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.xl,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		minWidth: 220,
	},
	navIconButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	// Neutralizes components-v2 Button's base box model (height/padding/centering/gap) so the
	// dense calendar micro-controls below can own their own layout while still using <Button>.
	resetButton: {
		height: 'auto',
		minHeight: 0,
		padding: 0,
		gap: 0,
		justifyContent: 'flex-start',
		textAlign: 'left',
		fontSize: 'inherit',
		fontFamily: 'inherit',
	},
	viewBody: {
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		display: 'flex',
	},
	loadingOverlay: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flex: 1,
		minHeight: 200,
	},

	// --- Month grid ---------------------------------------------------------
	monthGrid: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		overflow: 'hidden',
	},
	monthWeekdays: {
		display: 'flex',
		borderBottom: THEME.common.border,
	},
	monthWeekday: {
		flex: 1,
		padding: `${THEME.spacing.xs}px 0`,
		textAlign: 'center',
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	monthBody: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		...THEME.common.scrollBar,
	},
	monthWeekRow: {
		display: 'flex',
		flex: 1,
		minHeight: 96,
		borderTop: THEME.common.border,
		':first-child': {
			borderTop: 'none',
		},
	},
	monthDayCell: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		padding: THEME.spacing.xs,
		borderLeft: THEME.common.border,
		cursor: 'pointer',
		overflow: 'hidden',
		':first-child': {
			borderLeft: 'none',
		},
		':hover': {
			backgroundColor: THEME.colors.hover.light,
		},
	},
	monthDayCellOutside: {
		backgroundColor: THEME.colors.background.main,
	},
	monthDayNumber: {
		alignSelf: 'flex-end',
		minWidth: 20,
		height: 20,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		border: 'none',
		background: 'none',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
	},
	monthDayNumberOutside: {
		color: THEME.colors.text.light,
	},
	monthDayNumberToday: {
		borderRadius: '50%',
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	monthChips: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		minWidth: 0,
	},
	// Matches the week/day time-grid block: tinted background + colored left accent.
	// Background and accent color are set inline per item.
	// Overdue cue — a small red clock-alert before the title (keeps the chip its type color).
	overdueIcon: {
		display: 'inline-flex',
		alignItems: 'center',
		flexShrink: 0,
		marginRight: 3,
		verticalAlign: 'text-bottom',
		':has(svg) svg': {
			width: 12,
			height: 12,
		},
	},
	monthChip: {
		display: 'block',
		boxSizing: 'border-box',
		width: '100%',
		textAlign: 'left',
		padding: '1px 6px',
		borderRadius: THEME.common.borderRadius,
		borderLeft: '3px solid transparent',
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.xxs,
		lineHeight: 1.4,
		...THEME.common.truncate,
		':hover': {
			filter: 'brightness(0.97)',
		},
	},
	monthMore: {
		padding: '1px 4px',
		textAlign: 'left',
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
		cursor: 'pointer',
		':hover': {
			textDecoration: 'underline',
		},
	},

	// --- Time grid (week + day) ---------------------------------------------
	// All-day band pinned above the hourly grid (Google-style), for timeless items.
	allDayRow: {
		display: 'flex',
		borderBottom: THEME.common.border,
		minHeight: 28,
		maxHeight: 96,
		overflowY: 'auto',
		// Match the hourly grid's reserved 8px scrollbar width so the columns stay aligned.
		paddingRight: THEME.spacing.sm,
		...THEME.common.scrollBar,
	},
	// Day view lets the band grow taller when expanded, but still bounded + scrollable so a heavy
	// day can't push the hourly grid off-screen.
	allDayRowExpanded: {
		maxHeight: 240,
	},
	// The gutter is a fixed-width flex item, so border-box keeps it exactly 64px (matching the
	// header gutter / hours axis) regardless of its padding.
	allDayGutter: {
		boxSizing: 'border-box',
		flex: '0 0 auto',
		width: 64,
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
		textAlign: 'right',
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	// Match the header day cell's box model exactly (content-box, vertical-only padding,
	// left border) so the all-day columns line up with the header and hourly-grid columns.
	allDayCell: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		padding: `${THEME.spacing.xs}px 0`,
		borderLeft: THEME.common.border,
	},
	// Inset the all-day chip within its cell via margin (not cell padding, which would break
	// column alignment) so the bar doesn't butt against the divider or the grid's right edge —
	// mirroring the 2px inset the timed blocks use.
	allDayChip: {
		width: 'auto',
		marginLeft: 2,
		marginRight: 2,
		padding: '2px 6px',
		// Keep the chip's natural height so a busy day's band scrolls instead of squishing chips
		// into unreadable slivers (the cell is a fixed-height flex column).
		flexShrink: 0,
	},
	// The all-day "+N more" roll-up: same inset as the chips it collapses.
	allDayMore: {
		marginLeft: 2,
		marginRight: 2,
		padding: '1px 6px',
		flexShrink: 0,
	},
	// The expanded "Show less" control pins to the bottom of the scrolling band so it's reachable
	// without scrolling past every chip. Full-width opaque backing so chips don't bleed behind it.
	allDayCollapse: {
		position: 'sticky',
		bottom: 0,
		alignSelf: 'stretch',
		marginLeft: 0,
		marginRight: 0,
		background: THEME.colors.background.light,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	gridWrap: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		overflow: 'hidden',
	},
	gridHeaderRow: {
		display: 'flex',
		borderBottom: THEME.common.border,
		// The hourly grid below always shows an 8px scrollbar (see scrollBar); reserve the same
		// width here so these day columns line up with the scrolled grid's columns.
		paddingRight: THEME.spacing.sm,
	},
	gridHeaderGutter: {
		flex: '0 0 auto',
		width: 64,
	},
	gridHeaderDay: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		padding: `${THEME.spacing.sm}px 0`,
		borderLeft: THEME.common.border,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	gridHeaderDayName: {
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	gridHeaderDayNumber: {
		marginTop: 2,
		minWidth: 28,
		height: 28,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
	},
	gridHeaderDayNumberToday: {
		borderRadius: '50%',
		backgroundColor: THEME.colors.primary.main,
		color: THEME.colors.primary.contrastText,
	},
	gridHeaderToday: {
		color: THEME.colors.primary.main,
	},
	gridScroll: {
		flex: 1,
		overflowY: 'auto',
		// Always reserve the scrollbar gutter (8px) whether or not the grid actually scrolls, so
		// the day columns keep the same usable width as the header/all-day rows (which reserve the
		// same 8px) regardless of the host layout — e.g. the Action Items view isn't height-bound,
		// so its grid wouldn't otherwise show a scrollbar.
		scrollbarGutter: 'stable',
		...THEME.common.scrollBar,
	},
	gridInner: {
		display: 'flex',
		position: 'relative',
	},
	hoursAxis: {
		flex: '0 0 auto',
		width: 64,
		position: 'relative',
	},
	hourLabel: {
		position: 'absolute',
		right: THEME.spacing.sm,
		transform: 'translateY(-50%)',
		fontSize: THEME.typography.fontSizes.xxs,
		color: THEME.colors.text.light,
	},
	daysContainer: {
		display: 'flex',
		flex: 1,
		position: 'relative',
	},
	dayColumn: {
		flex: 1,
		position: 'relative',
		borderLeft: THEME.common.border,
	},
	hourLine: {
		position: 'absolute',
		left: 0,
		right: 0,
		borderTop: `1px solid ${THEME.colors.border.light}`,
	},
	nowLine: {
		position: 'absolute',
		left: 0,
		right: 0,
		height: 0,
		borderTop: `2px solid ${THEME.colors.danger.main}`,
		zIndex: 3,
	},
	meetingBlock: {
		position: 'absolute',
		boxSizing: 'border-box',
		// Block (not Button's base flex) so the two-line time-over-title layout stacks.
		display: 'block',
		borderRadius: THEME.common.borderRadius,
		padding: `2px ${THEME.spacing.xs}px`,
		overflow: 'hidden',
		cursor: 'pointer',
		borderLeft: '3px solid transparent',
		fontSize: THEME.typography.fontSizes.xxs,
		lineHeight: 1.25,
		zIndex: 2,
		':hover': {
			filter: 'brightness(0.97)',
		},
	},
	meetingBlockTime: {
		display: 'block',
		color: THEME.colors.text.light,
		...THEME.common.truncate,
	},
	// Compact single-line layout for short events: time + title on one truncated line.
	meetingBlockCompact: {
		...THEME.common.truncate,
	},
	meetingBlockTimeInline: {
		marginRight: THEME.spacing.xs,
		color: THEME.colors.text.light,
	},
	meetingBlockTitleInline: {
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	meetingBlockTitle: {
		display: 'block',
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		...THEME.common.truncate,
	},
	cancelledText: {
		textDecoration: 'line-through',
		opacity: 0.7,
	},
	// Whole-chip fade for past meetings — same type color, just muted.
	faded: {
		opacity: 0.5,
	},
});
